<template>
  <div class="image-wrapper">
    <van-swipe
      class="my-swipe"
      indicator-color="white"
      :show-indicators="false"
      :initial-swipe="initialSwipeIndex"
      @change="handleSwipeChange"
    >
      
      <van-swipe-item v-if="videoUrl && !skuChange">
        <div class="swipe-image">
          <div @click="handlePlayerClick" v-if="!KVideoPlayerShow">
            <img class="img-content" :src="$cvWebpFn(currentSwiperImgs[0], { w: 960 })" loading="eager" fetchpriority="high" :alt="imgTitle" @load="onImageLoad(0)">
            <span class="img-content loading-box"><van-icon name="play-circle-o" size="56" color="#fff" /></span>
          </div>
          
          <KVideoPlayer v-else :autoPlay="true" :src="videoUrl" class="img-content" />
          <!-- <KVideoPlayer v-else :autoPlay="true" src="https://video.kilimall.com/dash/392aa9e077175e3a43792eedc3757edd/play/index.mpd" class="img-content" /> -->
        </div>
      </van-swipe-item>
      <van-swipe-item
        v-for="(src, idx) in currentSwiperImgs"
        :key="idx"
        @mousemove="handleMouseMoveClick"
        @mouseenter="handleMouseEnterClick"
        @mouseout="handleMouseOverClick"
      >
        <div class="swipe-image">
          <img v-if="preparedImages.has(idx + (videoUrl && !skuChange ? 1 : 0))" class="img-content" :src="$cvWebpFn(src, { w: 960 })" loading="eager" :fetchpriority="!videoUrl && idx === 0 ? 'high' : 'low'" :alt="imgTitle" @load="onImageLoad(idx + (videoUrl && !skuChange ? 1 : 0))">
        </div>
      </van-swipe-item>
    </van-swipe>

    <div
      v-show="zoomShow"
      class="hover-box"
      :style="{ left: `${hoverBox.x}px`, top: `${hoverBox.y}px` }"
    />

    <div class="zoom swipe-image" v-show="zoomShow">
      <img 
        v-lazy="$cvWebpFn(zoomImg)"
        class="zoom-image"
        :alt="imgTitle"
        :style="{
          marginLeft: `${zoomImagePos.marginLeft}px`,
          marginTop: `${zoomImagePos.marginTop}px`
        }"
      >
    </div>
  </div>
  <div class="thumbnails">
    <div class="img-item video-item" v-if="videoUrl">
      <img
        :src="$cvWebpFn(images[0], { w: 240, removeTag: true })"
        loading="lazy"
        :data-state="initialSwipeIndex === 0 ? 'act' : 'default'"
        :alt="imgTitle"
        @mouseenter="handleThumbnailsEnter(0)"
      >
      <div class="player-icon">
        <van-icon name="play-circle-o" size="40" color="#fff" />
      </div>
    </div>
    <div
      v-for="(src, idx) in images"
      :key="idx"
      class="img-item"
    >
      <img
        @mouseenter="handleThumbnailsEnter(videoUrl ? idx + 1 : idx)"
        :data-state="(videoUrl ? idx + 1 : idx) === initialSwipeIndex ? 'act' : 'default'"
        :src="$cvWebpFn(src, { w: 240, removeTag: true })"
        :alt="imgTitle"
        loading="lazy"
      >
    </div>
  </div>
</template>

<script setup lang="ts">
const KVideoPlayer = defineAsyncComponent(() => import('@/components/KVideoPlayer.client.vue'))
const props = defineProps<{
  images: string[],
  videoUrl?: string
  currentSkuImg?: string
  imgTitle?: string
}>()

const { $throttle } = useNuxtApp()
const zoomImg = computed<string>(() => {
  const index = props.videoUrl ? initialSwipeIndex.value - 1 : initialSwipeIndex.value
  return currentSwiperImgs.value[index] || ''
})

const swipeImgs = ref<string[]>(props.images)
const currentSwiperImgs = ref<string[]>([...swipeImgs.value])
const skuChange = ref<boolean>(false)
const slides = computed(() => props.videoUrl && !skuChange.value
  ? [props.videoUrl, ...currentSwiperImgs.value] : currentSwiperImgs.value)
const { preparedImages, prepareImages, onImageLoad } = useSwipeImageLoading(slides)
const handleSwipeChange = (index: number) => {
  initialSwipeIndex.value = index
  prepareImages(index)
}
watch(
  () => [props.currentSkuImg, props.images],
  () => {
    const { currentSkuImg, images } = props
    skuChange.value = false
    if(currentSkuImg) {
      initialSwipeIndex.value = 0
      currentSwiperImgs.value = [currentSkuImg]
      skuChange.value = true
    } else {
      currentSwiperImgs.value = [...images]
    }
  }
)

ref<string>(props.images[0] || '')
const zoomShow = ref<boolean>(false)
const hoverBox = reactive<{ x: number, y: number }>({ x: 0, y: 0 })
const zoomImagePos = reactive<{ marginLeft: number, marginTop: number }>({ marginLeft: 0, marginTop: 0 })

let imageWrapper = ref<HTMLDivElement>()
onMounted(() => {
  imageWrapper.value = document.getElementsByClassName('image-wrapper')[0] as HTMLDivElement
})
const handleMouseEnterClick = (e: MouseEvent) => {
  if(!imageWrapper.value) {
    return
  }
  
  zoomImagePos.marginLeft = imageWrapper.value!.clientWidth / 3.2
  zoomImagePos.marginTop = imageWrapper.value!.clientHeight / 3.2
  zoomShow.value = true
}
const handleMouseOverClick = (e: MouseEvent) => {
  zoomShow.value = false
}
const handleMouseMoveClick = $throttle(function(e: MouseEvent) {
  let x = e.offsetX - 112
  let y = e.offsetY - 112

  if(x < 0) { 
    x = 0 
  }
  if(x > imageWrapper.value!.clientWidth - 225) {
    x = imageWrapper.value!.clientWidth - 225
  }

  if(y < 0) {
    y = 0
  }
  if(y > imageWrapper.value!.clientHeight - 225) {
    y = imageWrapper.value!.clientHeight - 225
  }

  hoverBox.x = x
  hoverBox.y = y

  zoomImagePos.marginLeft = (imageWrapper.value!.clientWidth / 3.2 - (225 / 3.2)) - e.offsetX
  zoomImagePos.marginTop = (imageWrapper.value!.clientHeight / 3.2 - (225 / 3.2)) - e.offsetY
}, 5)

const initialSwipeIndex = ref<number>(0)
const handleThumbnailsEnter = async(idx: number) => {
  skuChange.value = false
  currentSwiperImgs.value = [...props.images]

  await nextTick()
  initialSwipeIndex.value = idx
  prepareImages(idx)
}

const KVideoPlayerShow = ref<boolean>(false)
const handlePlayerClick = async() => {
  KVideoPlayerShow.value = true
}
</script>

<style lang="scss" scoped>
.image-wrapper{
  position: relative;
  width: 100%;
}
.swipe-image{
  width: 100%;
  height: 0;
  padding-bottom: 100%;
  background-color: white;
  overflow: hidden;
  position: relative;
  .img-content{
    position: absolute;
    bottom: 0;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  .loading-box{
    // background-color: rgba($color: #fff, $alpha: 0.4);
    @include center();
  }
}
.zoom{
  position: absolute;
  left: 102%;
  top: 0;
  border: 1px solid #cacaca;
  z-index: 9999;
}
.zoom-image {
  width: 1440px;
  height: 1440px;
  object-fit: contain;
}
.mark{
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
}

.hover-box{
  position: absolute;
  width: 450px;
  height: 450px;
  pointer-events: none;
  background-color: rgba(0, 0, 0, .2);
}

.thumbnails{
  display: flex;
  flex-wrap: nowrap;
  width: 100%;
  overflow-y: auto;
  user-select: none;
  &::-webkit-scrollbar{
    width: 10px;
    background-color: antiquewhite;
  }
  &::-webkit-scrollbar {
    /*滚动条整体样式*/
    width : 20px;  /*高宽分别对应横竖滚动条的尺寸*/
    height: 15px;
  }
  &::-webkit-scrollbar-thumb {
    /*滚动条里面小方块*/
    border-radius: 10px;
    box-shadow   : inset 0 0 5px rgba(0, 0, 0, 0.2);
    background   : #cacaca;
    cursor: pointer;
  }
  &::-webkit-scrollbar-track {
    /*滚动条里面轨道*/
    box-shadow   : inset 0 0 5px rgba(0, 0, 0, 0.2);
    border-radius: 10px;
    background   : #ededed;
  }
  .img-item{
    padding: 10px;
    cursor: pointer;
    img{
      border: 2px solid #efefef;
      transition: border-color .15s;
      height: 160px;
      width: 160px;
      min-width: 160px;
      object-fit: contain;
      &:hover{
        border-color: var(--pc-default-color);
      }
    }
    img[data-state="act"] {
      border-color: var(--pc-default-color);
    }
  }
  .video-item{
    position: relative;
    .player-icon{
      position: absolute;
      top: 12px;
      left: 12px;
      bottom: 12px;
      right: 12px;
      cursor: pointer;
      @include center();
      background-color: rgba($color: #333, $alpha: .2);
    }
  }
}
</style>
